import React from 'react';
import {OperationsCtx} from 'components/dataProviders/operations';
import {CallsManager, CallEvents, CallStatus as ManagerCallStatus} from 'controllers/videocall/CallsManager';


const CallStatus = {
    Idle: 0,
    Incoming: 1,
    Calling: 2,
    Connecting: 3,
    Live: 4
}

Object.freeze(CallStatus);

const CallInfoCtx = React.createContext({
    info: {
        inCall: false,
        operationID: null, 
        caller: null,
        status: CallStatus.Idle,
        videoElements: {
            local: null,
            remote: null
        }
    },
    funcs:
    {
        call(webcam, operationID) {},
        acceptCall(webcam) {},
        endCall() {},
        rejectCall() {}
    }
});

function CallInfoProvider(props)
{
    const opsCtx = React.useContext(OperationsCtx);
    const [callInfo, setCallInfo] = React.useState({
        inCall: false,
        operationID: null,
        caller: null,
        status: CallStatus.Idle,
        videoElements: {
            local: null,
            remote: null
        }
    });

    const call = React.useCallback((webcam, operationID) => {
        let op = opsCtx.data.indexed[operationID];
        if(!op)
        {
            throw new Error("Unexpected error: Call initiated from unknown operation");
        }
        if(op.currentUserRole === "admin")
        {
            throw new Error("Unexpected error: Administrators can't call");
        }
        let callingUser = op.currentUserRole === "expert" ? op.operator : op.expert;
        CallsManager.Call(callingUser, operationID, webcam);

        let tmpCallInfo = {...callInfo};

        tmpCallInfo.inCall = true;
        tmpCallInfo.operationID = operationID;
        tmpCallInfo.caller = callingUser;
        tmpCallInfo.status = CallStatus.Calling;
        setCallInfo(tmpCallInfo);
    }, [opsCtx, callInfo]);

    const acceptCall = React.useCallback((webcam) =>
    {
        CallsManager.AcceptCall(webcam);
    }, []);

    const rejectCall = React.useCallback(() => {
        CallsManager.RejectCall();
    },[]);

    const endCall = React.useCallback(() => {
        CallsManager.EndCall();
    }, []);


    React.useEffect(() => {
       
       function handleIncomingCall({operationID, caller})
       {
           let tmpCallInfo = {...callInfo};

            tmpCallInfo.inCall = true;
            tmpCallInfo.operationID = operationID;
            tmpCallInfo.caller = caller;
            tmpCallInfo.status = CallStatus.Incoming;
            setCallInfo(tmpCallInfo);
       };

       function handleCallEnded()
       {
            let tmpCallInfo = {...callInfo};

            tmpCallInfo.inCall = false;
            tmpCallInfo.operationID = null;
            tmpCallInfo.caller = null;
            tmpCallInfo.status = CallStatus.Idle;
            tmpCallInfo.videoElements.local = null;
            tmpCallInfo.videoElements.remote = null;
            setCallInfo(tmpCallInfo);
       }

       function handleCallStatusChanged(newStatus)
       {
           if(newStatus === ManagerCallStatus.LIVE)
           {
                let tmpCallInfo = {...callInfo};
                tmpCallInfo.status = CallStatus.Live;
                setCallInfo(tmpCallInfo);
           }
       }

       function handleMediaChange()
        {
            let tmpCallInfo = {...callInfo};
            
            tmpCallInfo.videoElements.remote = CallsManager.remoteVideoElement;
            tmpCallInfo.videoElements.local = CallsManager.localVideoElement;
            setCallInfo(tmpCallInfo);
        }

       CallsManager.on(CallEvents.callIncoming, handleIncomingCall);
       CallsManager.on(CallEvents.callEnded, handleCallEnded);
       CallsManager.on(CallEvents.statusChanged, handleCallStatusChanged);
       CallsManager.on(CallEvents.mediaChanged, handleMediaChange);

       return () => {
           CallsManager.off(CallEvents.callIncoming, handleIncomingCall);
           CallsManager.off(CallEvents.callEnded, handleCallEnded);
           CallsManager.off(CallEvents.statusChanged, handleCallStatusChanged);
           CallsManager.off(CallEvents.mediaChanged, handleMediaChange);
       };
    }, [callInfo]);


    return <CallInfoCtx.Provider value={{info: callInfo, funcs: {call: call, acceptCall: acceptCall, rejectCall: rejectCall, endCall: endCall}}}>{props.children}</CallInfoCtx.Provider>
}

export {CallInfoCtx, CallInfoProvider, CallStatus};